"use client";
/**
 * Display Profiles — named "desktop configurations" (e.g. Laptop, External
 * Screen) that each keep their OWN per-page layout, so you can flip between
 * them in one click.
 *
 * Model — no copies, no drift (see lib/display/layoutNamespace):
 *   - A profile is just an { id, name }. Its layout lives in a physical
 *     namespace keyed by the id; the layout you edit while it's loaded IS that
 *     profile's layout. There is nothing to "save into" the profile — the
 *     page's own Save is the only save.
 *   - Profile DEFINITIONS (the id/name list) SYNC across machines (define once,
 *     available everywhere). The layout slots themselves are per-machine.
 *   - The ACTIVE profile id is PER-MACHINE (a non-`mpp:` key, so it never
 *     syncs) — each screen remembers which profile it's on.
 *   - Switching = point storage at the other namespace + reload. No capture,
 *     no apply, no bleed.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import { flushPrefsNow } from "@/lib/prefsSync";
import { loadPages } from "@/lib/workspace/workspacePages";
import {
  installLayoutNamespace, migrateLayoutNamespaces, namespaceLogicalKeys,
  purgeNamespace, readNamespaced, seedNamespaceFrom, setActiveNamespace,
} from "./layoutNamespace";

/** Synced: the profile definitions follow the user across machines. */
const PROFILES_KEY = "mpp:display-profiles:v1";
/** NOT synced (no `mpp:` prefix): which profile THIS machine uses. */
const ACTIVE_KEY = "displayProfileActiveId";
/** NOT synced: whether this machine auto-switches profiles by screen. */
const AUTO_KEY = "displayProfileAutoDetect";
/** NOT synced: `{ sig, id }` — the profile the user picked BY HAND for a given
 *  screen. A manual pick is an override of auto-switch, not a lesson for it. */
const MANUAL_KEY = "displayProfileManualPick";
/** Fired when the active profile changes, so mounted switchers re-read. */
export const PROFILE_APPLIED_EVENT = "mpp:display-profile-applied";

export type DisplayProfile = {
  id: string;
  name: string;
  updatedAt: number;
  /** Physical screen this profile is associated with, for auto-detect. Recorded
   *  when the profile is created or manually loaded (so loading it on a screen
   *  "teaches" it that screen). `w`/`h` are `window.screen.{width,height}`;
   *  `dpr` is `devicePixelRatio`, which separates two monitors that report the
   *  same CSS resolution at different scaling (e.g. a work 1080p at 1x vs a
   *  retina external that also reports 1920-wide at 2x). Optional — profiles
   *  recorded before dpr existed still match on dimensions alone. */
  screen?: { w: number; h: number; dpr?: number };
  /** Stack bands taller than the viewport and scroll the page. Laptop clones
   *  of an ultrawide inventory use this so six charts stay readable. */
  scrollCanvas?: boolean;
  /** Seed generation for the arranged-Retina clone's per-page trees. */
  treesSeeded?: number;
  /** Legacy copy from the old snapshot model — read only by migration. */
  snapshot?: Record<string, string>;
};

function newId(): string {
  return `dp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function loadProfiles(): DisplayProfile[] {
  try {
    const raw = safeLocalStorageGet(PROFILES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as DisplayProfile[];
    return Array.isArray(arr)
      ? arr.filter((p) => p && typeof p.id === "string" && typeof p.name === "string")
      : [];
  } catch {
    return [];
  }
}

export function saveProfiles(list: DisplayProfile[]): void {
  safeLocalStorageSet(PROFILES_KEY, JSON.stringify(list));
}

export function getActiveProfileId(): string | null {
  const v = safeLocalStorageGet(ACTIVE_KEY);
  return v && v.length ? v : null;
}

export function setActiveProfileId(id: string | null): void {
  safeLocalStorageSet(ACTIVE_KEY, id ?? "");
  // Point the storage namespace at this profile immediately, so writes that
  // happen before the caller's reload already land in the right slot.
  setActiveNamespace(id ?? null);
  try {
    window.dispatchEvent(new CustomEvent(PROFILE_APPLIED_EVENT, { detail: { id } }));
  } catch {
    /* SSR */
  }
}

/**
 * Install the localStorage namespace patch. Must run EARLY — before prefsSync
 * and any workspace patch setItem, and before any layout component mounts — so
 * every layout read/write is remapped. Idempotent. Does NOT migrate or activate
 * yet: the profile definitions (and their legacy snapshots) aren't in
 * localStorage until hydratePrefs pulls them from the server.
 */
let installedGate = false;
export function installDisplayProfiles(): void {
  if (installedGate || typeof window === "undefined") return;
  // Wrapped: this patches localStorage before the app renders — it must never
  // throw out to <PrefsSyncGate>, or the render gate never lifts (blank app).
  try {
    installLayoutNamespace();
    installedGate = true;
  } catch {
    /* run un-namespaced (shared layout) rather than blank the app */
  }
}

/**
 * Run the one-time copy→namespace migration and point storage at this machine's
 * active profile. Call AFTER hydratePrefs resolves, so the synced profile
 * definitions (with their legacy snapshots) are present to seed from — and
 * BEFORE the app's layout components mount. Idempotent within a page load.
 */
let appliedGate = false;
export function applyDisplayProfilesAfterHydrate(): void {
  if (appliedGate || typeof window === "undefined") return;
  appliedGate = true;
  // Every step is independently guarded: migration must never block render, and
  // even if it does nothing, activating the profile is safe — the overlay read
  // model falls back to the shared layout for any key the profile lacks.
  let active: string | null = null;
  try { active = getActiveProfileId(); } catch { /* ignore */ }
  try { migrateLayoutNamespaces(loadProfiles(), active); } catch { /* overlay covers a partial/failed migration */ }
  // Auto-detect: if on and a profile matches the screen the window is on, make
  // it active BEFORE components mount, so the right layout loads with no reload.
  try { if (autoApplyForCurrentScreen()) active = getActiveProfileId(); } catch { /* ignore */ }
  try { setActiveNamespace(active); } catch { /* ignore */ }
}

/**
 * Create a profile from the CURRENT layout: clone what's on screen into a new
 * namespace and switch to it. Editing from here on writes to the new profile.
 * Records the current screen so auto-detect can match it later.
 */
export function createFromCurrent(name: string): { list: DisplayProfile[]; profile: DisplayProfile } {
  const profile: DisplayProfile = { id: newId(), name: name.trim() || "Untitled", updatedAt: Date.now(), screen: screenSignature() ?? undefined };
  seedNamespaceFrom(getActiveProfileId(), profile.id);
  const list = [...loadProfiles(), profile];
  saveProfiles(list);
  setActiveProfileId(profile.id);
  try { rememberManualPick(profile.id); } catch { /* ignore */ }
  return { list, profile };
}

// ── Auto-detect by screen ────────────────────────────────────────────────────

export function isAutoDetect(): boolean {
  return safeLocalStorageGet(AUTO_KEY) === "1";
}
export function setAutoDetect(on: boolean): void {
  safeLocalStorageSet(AUTO_KEY, on ? "1" : "0");
}

/** The physical screen the window is currently on: CSS dimensions plus the
 *  display's scaling, which is what tells two same-resolution monitors apart. */
export function screenSignature(): { w: number; h: number; dpr?: number } | null {
  try {
    if (typeof window === "undefined" || !window.screen) return null;
    const w = Math.round(window.screen.width);
    const h = Math.round(window.screen.height);
    if (!w || !h) return null;
    const raw = window.devicePixelRatio;
    const dpr = typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.round(raw * 100) / 100 : undefined;
    return dpr == null ? { w, h } : { w, h, dpr };
  } catch {
    return null;
  }
}

/** Tolerances: dpr is reported to 2dp and can jitter slightly; dimensions get a
 *  small allowance for OS chrome differences. */
const DPR_TOL = 0.05;
const DIM_TOL = 300;

function sameDpr(a: number | undefined, b: number | undefined): boolean {
  if (a == null || b == null) return true; // unknown on either side — don't discriminate
  return Math.abs(a - b) <= DPR_TOL;
}

/** Record the current screen as profile `id`'s. This is the EXPLICIT "use this
 *  screen" / "retag" action in Settings — the only thing that writes a screen
 *  association, so merely loading a profile can never mislabel it. */
export function associateCurrentScreen(id: string): void {
  const sig = screenSignature();
  if (!sig) return;
  const list = loadProfiles().map((p) => (p.id === id ? { ...p, screen: sig } : p));
  saveProfiles(list);
  // Retagging is the user telling auto-switch what belongs on this screen, so
  // drop any hand-pick override here and let the match apply again.
  clearManualPick();
}

/** A stable name for the screen the window is on, used to scope a manual pick
 *  to the screen it was made on (moving monitors re-enables auto-switch). */
function currentScreenId(): string | null {
  const s = screenSignature();
  return s ? `${s.w}x${s.h}@${s.dpr ?? "?"}` : null;
}

/** Remember that the user hand-picked `id` while on this screen. */
function rememberManualPick(id: string): void {
  const sig = currentScreenId();
  if (!sig) return;
  safeLocalStorageSet(MANUAL_KEY, JSON.stringify({ sig, id }));
}

/** The profile the user hand-picked for THIS screen, if any. */
export function manualPickForCurrentScreen(): string | null {
  try {
    const raw = safeLocalStorageGet(MANUAL_KEY);
    if (!raw) return null;
    const { sig, id } = JSON.parse(raw) as { sig?: string; id?: string };
    return sig && id && sig === currentScreenId() ? id : null;
  } catch {
    return null;
  }
}

/** Forget the hand-pick for this screen — auto-switch decides again. */
export function clearManualPick(): void {
  safeLocalStorageSet(MANUAL_KEY, "");
}

/**
 * The profile whose recorded screen best matches the one the window is on now,
 * or null when we can't tell confidently — auto-detect must never force a
 * wrong-screen layout, so "don't know" leaves the active profile alone.
 *
 * Two monitors at the SAME css resolution (a home and a work screen of the same
 * size) are separated by devicePixelRatio. If even that can't split them, we
 * return null rather than guess, and the user switches manually.
 */
export function matchProfileToScreen(): DisplayProfile | null {
  const sig = screenSignature();
  if (!sig) return null;
  const list = loadProfiles().filter((p) => p.screen);
  if (!list.length) return null;

  // 1) Same pixel dimensions — the common case.
  const sameDims = list.filter((p) => p.screen!.w === sig.w && p.screen!.h === sig.h);
  if (sameDims.length === 1) return sameDims[0]!;
  if (sameDims.length > 1) {
    // Several profiles claim this resolution: let scaling break the tie.
    const byDpr = sameDims.filter((p) => p.screen!.dpr != null && sig.dpr != null && Math.abs(p.screen!.dpr! - sig.dpr) <= DPR_TOL);
    if (byDpr.length === 1) return byDpr[0]!;
    return null; // genuinely ambiguous — don't guess
  }

  // 2) No exact size: nearest within tolerance, with a penalty when the scaling
  //    disagrees so a same-size-different-dpr screen isn't preferred blindly.
  let best: DisplayProfile | null = null;
  let bestD = Infinity;
  for (const p of list) {
    const d = Math.abs(p.screen!.w - sig.w) + Math.abs(p.screen!.h - sig.h) + (sameDpr(p.screen!.dpr, sig.dpr) ? 0 : 150);
    if (d < bestD) { bestD = d; best = p; }
  }
  return bestD <= DIM_TOL ? best : null;
}

/** If auto-detect is on and a profile matches the current screen, make it the
 *  active one. Returns true if the active profile changed. Safe pre-render (no
 *  reload needed on initial load — components read the newly-active namespace). */
export function autoApplyForCurrentScreen(): boolean {
  if (!isAutoDetect()) return false;
  // A hand-pick on THIS screen wins. Without this the match re-applies on every
  // load and silently undoes the switcher: you click a profile, the reload puts
  // you straight back on the matched one, and the layout you saved under the
  // profile you picked looks lost. Moving to another monitor changes the screen
  // id, so auto-switch takes over again there.
  if (manualPickForCurrentScreen()) return false;
  const m = matchProfileToScreen();
  if (!m || getActiveProfileId() === m.id) return false;
  setActiveProfileId(m.id);
  return true;
}

export function renameProfile(id: string, name: string): DisplayProfile[] {
  const list = loadProfiles().map((p) => (p.id === id ? { ...p, name: name.trim() || p.name, updatedAt: Date.now() } : p));
  saveProfiles(list);
  return list;
}

export function deleteProfile(id: string): DisplayProfile[] {
  purgeNamespace(id);
  const list = loadProfiles().filter((p) => p.id !== id);
  saveProfiles(list);
  if (getActiveProfileId() === id) setActiveProfileId(null);
  return list;
}

/**
 * Switch to a profile: point storage at its namespace. The caller reloads so
 * the grid / resizable panels remount and read the new slot. No snapshot to
 * capture — the outgoing profile's layout is already stored in its own slot.
 */
export function switchProfile(p: DisplayProfile): void {
  setActiveProfileId(p.id);
  // Picking a profile is an OVERRIDE for this screen, not a lesson about it. It
  // deliberately does NOT record the screen: re-tagging on every manual load is
  // what wrote a 1728x1117 MacBook signature onto the "LG - 27'" profile, after
  // which auto-switch matched it on the laptop and forced it back on every load.
  // Screens are tagged only by the explicit "use this screen" / "retag" button.
  try { rememberManualPick(p.id); } catch { /* ignore */ }
  // The caller reloads immediately. Push the pending prefs batch now, or the
  // reload's hydrate pulls the older server copy back over anything written in
  // the last debounce window — including a page you just saved.
  try { flushPrefsNow(); } catch { /* ignore */ }
}

/** Human summary — which pages/layouts this profile's namespace holds. */
/** True when the ACTIVE profile wants a scrollable workspace canvas. */
export function isActiveScrollCanvas(): boolean {
  const id = getActiveProfileId();
  if (!id) return false;
  return Boolean(loadProfiles().find((p) => p.id === id)?.scrollCanvas);
}

export function summarize(p: DisplayProfile): { pages: number; layouts: string[] } {
  const pool = loadPages();
  const byId = new Map(pool.map((w) => [w.id, w.name]));
  const keys = namespaceLogicalKeys(p.id);
  const selectionKeys = keys.filter((k) => k.startsWith("mpp:nested-ws:") && k.endsWith(":active:v1"));
  const layouts: string[] = [];
  for (const k of selectionKeys) {
    const id = readNamespaced(p.id, k); // this profile's own slot, not the active one
    const name = id ? byId.get(id) : undefined;
    if (name && !layouts.includes(name)) layouts.push(name);
  }
  return { pages: keys.length, layouts };
}
